import React from 'react';

import Button from '../../library/button/button';
import CarMirrorsSolidIcon from '../../shared/icons/car-mirrors-solid-icon';
import Message from '../message/message';

import { useProductSearchNavigation } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics.js';

import { PLPInfoMessageIds } from '../../pages/product-list-page/types/plp-products';
import { Fitment } from '../../shared/types/fitment';
import { ProductTypeEnum } from '../../shared/types/graphql-types';
import { PRODUCT_TYPES } from '../../shared/constants/products';
import { SEARCH_TYPES } from '../../shared/constants/search-types';

import './fit-message.one-dt.scss';

interface FitMessageProps {
  doesFit: boolean;
  fitment: Fitment;
  infoMessage?: { id: number };
  isLoading?: boolean;
  isPackage?: boolean;
  productType?: ProductTypeEnum | string;
  showFitmentDetails?: boolean;
}

function FitMessage(props: FitMessageProps) {
  const {
    doesFit = false,
    fitment,
    infoMessage,
    isLoading,
    isPackage,
    productType = PRODUCT_TYPES.TIRES,
    showFitmentDetails = false
  } = props;

  const { onSearchTypeSelected, onShopAllAccessories, onShopAllTires } =
    useProductSearchNavigation({
      fitment
    });

  const fitmentDetails = fitment
    ? `${fitment.year ?? ''} ${fitment.make ?? ''} ${fitment.model ?? ''} ${
        fitment.trim ?? ''
      }`.trim()
    : null;

  let productMessage = 'tires';
  let fitmentActionMessage = '';
  let isWarning = false;
  let isSolidInfo = false;
  let isError = false;
  let isSuccess = false;
  let message = '';
  let fitmentAction;
  let messageType = '';

  function handleEnterYourVehicleClick() {
    const locationHash = '#/fitment/vehicle/continue-to/close';
    trackAction('product_fitment_validation_banner');
    window.location.hash = locationHash;
  }

  function handleShopProductsThatFitClick() {
    trackAction('product_fitment_validation');
    if (productType === PRODUCT_TYPES.TIRES) {
      onShopAllTires();
    } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
      onShopAllAccessories();
    } else {
      onSearchTypeSelected(SEARCH_TYPES.WHEELS);
    }
  }

  if (PRODUCT_TYPES[productType] === PRODUCT_TYPES.ACCESSORIES) {
    productMessage = 'products';
  } else if (PRODUCT_TYPES[productType] === PRODUCT_TYPES.WHEELS) {
    productMessage = 'wheels';
  }

  if (!fitment) {
    fitmentActionMessage = 'Enter your vehicle';
    fitmentAction = handleEnterYourVehicleClick;
    isWarning = true;
    message = `to ensure these ${productMessage} fit`;
  }

  if (!isLoading && fitment) {
    if (productType === PRODUCT_TYPES.TIRES && infoMessage) {
      fitmentAction = null;
      fitmentActionMessage = '';
      isSolidInfo = true;
      message =
        infoMessage.id === PLPInfoMessageIds.noInStockTreadwellProducts
          ? 'There are no Treadwell tested tires in stock at your store, but here are the best tires for your size!'
          : "While Treadwell hasn't tested tires for this vehicle yet, these tires are a great fit!";
    } else if (doesFit || isPackage) {
      fitmentAction = null;
      fitmentActionMessage = '';
      isSuccess = true;
      messageType = 'success';
      message = isPackage
        ? `This package fits your ${!showFitmentDetails ? 'vehicle' : ''}`
        : `These ${productMessage} fit your ${
            !showFitmentDetails ? 'vehicle' : ''
          }`;
    } else if (productType === PRODUCT_TYPES.TIRES && !doesFit) {
      isWarning = true;
      messageType = 'warning';
      message = `These tires may not fit ${
        !showFitmentDetails ? 'your vehicle' : ''
      }`;
      fitmentActionMessage = `shop ${productMessage} that fit`;
      fitmentAction = handleShopProductsThatFitClick;
    } else {
      fitmentAction = handleShopProductsThatFitClick;
      fitmentActionMessage = `shop ${productMessage} that fit`;
      isError = true;
      messageType = 'error';
      message = `These ${productMessage} don't fit ${
        !showFitmentDetails ? 'your vehicle' : ''
      }`;
    }
  }

  return (
    <div className="fit-message" styleName="container">
      <Message
        isAlignCenter
        isError={isError}
        isSolidInfo={isSolidInfo}
        isSuccess={isSuccess}
        isWarning={isWarning}
        message={
          <>
            {message && (
              <>
                {!fitment && fitmentAction && (
                  <Button
                    onClick={fitmentAction}
                    size="regular"
                    styleName="button"
                  >
                    {fitmentActionMessage}
                    &nbsp;
                  </Button>
                )}
                {message}
                {showFitmentDetails && (
                  <div styleName="fitment-details">
                    <CarMirrorsSolidIcon styleName="icon" />
                    {fitmentDetails}
                  </div>
                )}
                {fitment && fitmentAction && (
                  <>
                    &nbsp;&nbsp;
                    <Button
                      className={`fit-message__message-link--${messageType}`}
                      onClick={fitmentAction}
                      size="regular"
                    >
                      {fitmentActionMessage}
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        }
      />
    </div>
  );
}

export default FitMessage;
